// Typed client for the backend API. All calls are same-origin and rely on the
// session cookie, so `credentials: 'include'` is set everywhere.

export type ColorMetric = 'generate_tps' | 'think_tps' | 'prefill_tps' | 'ttft_ms';
export type RunStatus = 'pending' | 'running' | 'done' | 'error' | 'cancelled';

export interface AppConfig {
  matrix: {
    inputTokens: number[];
    outputTokens: number[];
    colorMetric: ColorMetric;
    repetitions: number;
  };
  context: { defaultMaxKv: number; safetyMargin: number };
  run: { autoAdvanceDefault: boolean; autoAdvanceDelaySeconds: number };
  auth: { oidcConfigured: boolean; devBypass: boolean; testerGroup: string | null };
  openwebui: { baseUrl: string };
  hardware: string | null;
}

export interface SessionUser {
  sub: string;
  email: string | null;
  name: string | null;
  groups: string[];
  isTester: boolean;
}

export interface OverviewEntry {
  model: string;
  displayName: string;
  maxKv: number | null;
  lastTestedAt: string | null;
  lastStatus: string | null;
  score: number | null;
  lastRunId: string | null;
  benchmarks: Record<string, number> | null;
  prefillTps: number | null;
  thinkTps: number | null;
  generateTps: number | null;
  ttftMs: number | null;
  gpuPercent: number | null;
}

export interface Overview {
  colorMetric: ColorMetric;
  hardware: string | null;
  ollamaVersion: string | null;
  benchmarksFetchedAt: string | null;
  benchmarkLinks: Record<string, string>;
  models: OverviewEntry[];
}

export interface CatalogModel {
  name: string;
  displayName: string;
  maxKv: number | null;
  contextSource: string | null;
  lastTestedAt: string | null;
  lastStatus: string | null;
  lastScore: number | null;
  lastRunId: string | null;
}

export interface MatrixCell {
  inputTokens: number;
  outputTokens: number;
  nOk: number;
  skipped: boolean;
  skipReason?: string;
  errored?: boolean;
  errorReason?: string;
  value: number | null;
  meanGenerateTps?: number;
  medianGenerateTps?: number;
  stddevGenerateTps?: number;
  meanThinkTps?: number;
  meanPrefillTps?: number;
  meanTtftMs?: number;
  meanTotalMs?: number;
}

export interface RunSummary {
  colorMetric: ColorMetric;
  score?: number;
  meanPrefillTps?: number;
  meanThinkTps?: number;
  meanGenerateTps?: number;
  meanTtftMs?: number;
  cellsTotal: number;
  cellsMeasured: number;
  cellsSkipped: number;
  cellsErrored: number;
  placement?: { gpuPercent: number; sizeBytes: number; vramBytes: number } | null;
}

export interface ModelResult {
  model: string;
  runId: string | null;
  status: string | null;
  colorMetric: ColorMetric;
  inputTokens: number[];
  outputTokens: number[];
  maxKv: number | null;
  hardwareLabel: string | null;
  benchmarks: Record<string, number> | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  summary: RunSummary | null;
  cells: MatrixCell[];
  valueRange: { min: number | null; max: number | null };
  history: Array<{
    runId: string;
    status: string;
    queuedAt: string;
    finishedAt: string | null;
    score: number | null;
  }>;
}

export interface BatchModelState {
  model: string;
  index: number;
  status: RunStatus | 'queued';
  runId: string | null;
  score: number | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface MatrixOverrides {
  inputTokens?: number[];
  outputTokens?: number[];
  colorMetric?: ColorMetric;
  repetitions?: number;
  safetyMargin?: number;
  maxKv?: number | null;
}

export interface MatrixConfigSnapshot {
  inputTokens: number[];
  outputTokens: number[];
  colorMetric: ColorMetric;
  repetitions: number;
  safetyMargin: number;
  maxKv: number | null;
  hardwareLabel: string | null;
}

export interface BatchState {
  id: string;
  status: string;
  autoAdvance: boolean;
  delaySeconds: number;
  currentIndex: number;
  overrides: MatrixOverrides | null;
  models: BatchModelState[];
  currentRun: {
    id: string;
    model_name: string;
    status: RunStatus;
    summary: RunSummary | null;
    config_snapshot: MatrixConfigSnapshot;
  } | null;
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  // Only send a JSON content-type when there is actually a body — Fastify
  // rejects an empty body that declares `application/json` (e.g. POST /logout).
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) };
  if (init?.body != null) headers['Content-Type'] = 'application/json';
  const res = await fetch(url, { credentials: 'include', ...init, headers });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  config: () => req<AppConfig>('/api/config'),
  me: () => req<{ user: SessionUser | null }>('/api/auth/me'),
  logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  overview: () => req<Overview>('/api/results'),
  setHardware: (label: string) =>
    req<{ hardware: string | null }>('/api/settings/hardware', {
      method: 'PUT',
      body: JSON.stringify({ label }),
    }),
  modelResult: (model: string, runId?: string) =>
    req<ModelResult>(
      `/api/results/${encodeURIComponent(model)}${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`,
    ),
  models: () => req<{ models: CatalogModel[]; warning: string | null }>('/api/models'),
  startBatch: (body: {
    models: string[];
    autoAdvance: boolean;
    delaySeconds: number;
    overrides?: MatrixOverrides;
  }) => req<BatchState>('/api/test-batches', { method: 'POST', body: JSON.stringify(body) }),
  batch: (id: string) => req<BatchState>(`/api/test-batches/${id}`),
  currentBatch: () => req<{ batch: BatchState | null }>('/api/test-batches/current'),
  updateBatch: (id: string, body: { autoAdvance?: boolean; delaySeconds?: number }) =>
    req<BatchState>(`/api/test-batches/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  nextInBatch: (id: string) =>
    req<BatchState>(`/api/test-batches/${id}/next`, { method: 'POST' }),
  cancelBatch: (id: string) =>
    req<BatchState>(`/api/test-batches/${id}/cancel`, { method: 'POST' }),
};

export const loginUrl = '/api/auth/login';
